<script>
import preselection from "@/components/general/TextCheckConfig";
import SvgIcon from "@/assets/icon/index.vue";

export default {
  name: "TextCheck",
  components: { SvgIcon },
  data() {
    return {
      text1: "",
      text2: preselection[0]?.text || "",
      text3: "",
      key_point: preselection[0]?.key_point || "",
      preselection_index: 0,
      preselection,
      debounceTimer: null,
    };
  },
  mounted() {
    this.text_check();
  },
  beforeUnmount() {
    clearTimeout(this.debounceTimer);
  },
  methods: {
    // HTML 转义,防 XSS
    escapeHtml(str) {
      return String(str)
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&#39;");
    },
    escapeRegExp(string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    },
    // 统一高亮方法:输入已转义文本 → 返回带 span 的 HTML
    highlight(escapedText, keywords, className, delimiters = ",;|", caseSensitive = false) {
      if (!keywords) return escapedText;
      const fragments = keywords
          .split(new RegExp(`[${delimiters}]`))
          .map((f) => f.trim())
          .filter(Boolean);
      if (!fragments.length) return escapedText;

      const flags = caseSensitive ? "g" : "gi";
      const regex = new RegExp(`(${fragments.map(this.escapeRegExp).join("|")})`, flags);
      return escapedText.replace(regex, `<span class="${className}">$1</span>`);
    },
    text_check() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        const safe = this.escapeHtml(this.text1);
        let html = this.highlight(safe, this.text2, "hl-normal");
        if (this.key_point) {
          html = this.highlight(html, this.key_point, "hl-key");
        }
        this.text3 = html;
      }, 150);
    },
  },
  watch: {
    preselection_index() {
      const cur = this.preselection[this.preselection_index] || {};
      this.text2 = cur.text || "";
      this.key_point = cur.key_point || "";
      this.text_check();
    },
  },
};
</script>

<template>
  <div class="text-check">
    <!-- 左侧输入区 -->
    <div class="panel">
      <div class="field">
        <label class="field-label">需要检测的文本</label>
        <textarea
            v-model="text1"
            class="text-wb"
            placeholder="在此粘贴需要检测的文本…"
            @input="text_check"
        />
      </div>

      <div class="field">
        <label class="field-label">匹配词</label>
        <div class="quick-select">
          <span>快速选择</span>
          <select v-model="preselection_index">
            <option v-for="(item, index) in preselection" :value="index" :key="index">
              {{ item.name }}
            </option>
          </select>
        </div>
        <textarea
            v-model="text2"
            class="text-wb"
            placeholder="多个词用逗号 / 分号分隔…"
            @input="text_check"
        />
      </div>
    </div>

    <!-- 中间箭头 -->
    <div class="button-check" @click="text_check" title="检测">
      <svg-icon icon-class="shaperight" />
    </div>

    <!-- 右侧输出区 -->
    <div class="panel">
      <div class="field">
        <label class="field-label">输出结果</label>
        <div class="text-wb text-show" v-html="text3" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.text-check {
  display: flex;
  align-items: stretch;
  gap: 16px;
  width: 100%;
  height: 92vh;
  box-sizing: border-box;
  padding: 20px;
  background:
      radial-gradient(120% 60% at 50% 0%, rgba(42, 74, 184, 0.18) 0%, transparent 60%),
      #0a1230;
  border-radius: 16px;
}

.panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}

.field {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.field-label {
  font-size: 14px;
  letter-spacing: 1px;
  color: rgba(190, 210, 255, 0.75);
  margin-bottom: 8px;
}

.quick-select {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  font-size: 14px;
  color: rgba(190, 210, 255, 0.7);
}

.quick-select select {
  background: rgba(22, 38, 99, 0.6);
  color: #dbe6ff;
  border: 1px solid rgba(120, 160, 255, 0.35);
  border-radius: 8px;
  padding: 6px 10px;
  outline: none;
  cursor: pointer;
  transition: border-color 0.25s, box-shadow 0.25s;
}
.quick-select select:focus,
.quick-select select:hover {
  border-color: rgba(79, 209, 255, 0.8);
  box-shadow: 0 0 10px rgba(79, 141, 255, 0.4);
}

.text-wb {
  flex: 1;
  min-height:120px;
  margin-top: 4px;
  padding: 12px 14px;
  box-sizing: border-box;
  font-family: "Consolas", "Menlo", monospace, Arial, sans-serif;
  font-size: 16px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-y: auto;
  color: #e3ebff;
  background: rgba(10, 18, 48, 0.65);
  border: 1px solid rgba(120, 160, 255, 0.3);
  border-radius: 12px;
  outline: none;
  resize: vertical;
  transition: border-color 0.25s, box-shadow 0.25s, background 0.25s;
}

.text-wb::placeholder {
  color: rgba(160, 180, 230, 0.45);
}

.text-wb:focus {
  border-color: rgba(79, 209, 255, 0.85);
  box-shadow: 0 0 12px rgba(79, 141, 255, 0.45), inset 0 0 12px rgba(79, 141, 255, 0.12);
  background: rgba(10, 18, 48, 0.85);
}

.text-wb::-webkit-scrollbar { width: 6px; }
.text-wb::-webkit-scrollbar-track { background: rgba(120, 160, 255, 0.06); }
.text-wb::-webkit-scrollbar-thumb {
  background: rgba(120, 160, 255, 0.35);
  border-radius: 3px;
}
.text-wb::-webkit-scrollbar-thumb:hover { background: rgba(120, 200, 255, 0.55); }
.text-wb {
  scrollbar-width: thin;
  scrollbar-color: rgba(120, 160, 255, 0.35) rgba(120, 160, 255, 0.06);
}

.text-show {
  resize: none;
  cursor: default;
}

.button-check {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 56px;
  font-size: 42px;
  color: #4fd1ff;
  cursor: pointer;
  user-select: none;
  transition: transform 0.25s, text-shadow 0.25s;
}
.button-check:hover {
  transform: scale(1.12);
  text-shadow: 0 0 16px rgba(79, 209, 255, 0.9);
}
.button-check:active {
  transform: scale(0.96);
}

/* 高亮样式:用 :deep 穿透 v-html 生成的内容 */
:deep(.hl-normal) {
  background: rgba(255, 252, 47, 0.71);
  color: #eaf6ff;
  border-radius: 3px;
  padding: 0 2px;
  box-shadow: 0 0 6px rgba(79, 209, 255, 0.35);
}
:deep(.hl-key) {
  background: rgba(255, 12, 61, 0.85);
  color: #fff;
  border-radius: 3px;
  padding: 0 2px;
  box-shadow: 0 0 8px rgba(255, 86, 120, 0.6);
}

@media (max-width: 900px) {
  .text-check {
    flex-direction: column;
    height: auto;
  }
  .button-check {
    width: 100%;
    font-size: 32px;
    transform: rotate(90deg);
  }
  .button-check:hover {
    transform: rotate(90deg) scale(1.12);
  }
}

@media (prefers-reduced-motion: reduce) {
  * { transition: none !important; }
}
</style>